import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { fetchClientById, fetchClientsForMentions, labelClientsForPicker } from "../services/clients.service";
import { fetchMeetings, fetchMeetingsByClient, fetchMeetingSettings } from "../services/meetings.service";
import { fetchAllContacts } from "../services/contacts.service";
import { fetchAllDocTemplates } from "../services/docTemplates.service";
import { describeTriggerToken, isTriggerToken } from "./triggerVariableLabels";
import NotesFolderPicker from "./NotesFolderPicker";
import SkillLibraryField from "./SkillLibraryField";
import type { NodeDefArgField } from "./workflow.types";

interface PickerOption {
  id: string;
  label: string;
  /** Only for select-contact options — lets a field with `syncToField` (e.g.
   * send_sms's toPhoneNumber) auto-fill from the picked contact, since that
   * value must exactly match the contact's number on file. */
  phone?: string;
}

/** True for a real picked id, false for empty or a still-unresolved `{{...}}` template token. */
function isResolvedId(value: string | undefined): value is string {
  return !!value && !value.startsWith("{{");
}

/** A real Mongo ObjectId (24 hex chars) — as opposed to in-progress free-typed search text. */
function isMongoId(value: string): boolean {
  return /^[0-9a-f]{24}$/i.test(value);
}

// See RunWithMeetingPicker.tsx for why this bypasses MUI's own default
// filter — the same symptom (typing a real name still surfaces unrelated
// entries from a long list) showed up here too once actually tested.
function filterPickerOptions(options: PickerOption[], state: { inputValue: string }): PickerOption[] {
  const query = state.inputValue.trim().toLowerCase();
  if (!query) return options.slice(0, 100);
  return options.filter((o) => o.label.toLowerCase().includes(query)).slice(0, 100);
}

/** Options list always includes whatever {{trigger.x}}/{{steps.x...}} token the field defaulted to, so picking a real value never loses that shortcut. */
function withTemplateOption(options: PickerOption[], field: NodeDefArgField): PickerOption[] {
  if (!isTriggerToken(field.defaultTemplate)) return options;
  const label = `Use ${describeTriggerToken(field.defaultTemplate)}`;
  return [{ id: field.defaultTemplate, label }, ...options];
}

/** One argFields entry, rendered per its `type`. Single-value fields fetch their option list lazily on mount. */
export default function ActionArgField({
  field,
  value,
  onChange,
  scopeValue,
}: {
  field: NodeDefArgField;
  value: string;
  onChange: (value: string, extra?: Record<string, string>) => void;
  /** Current value of the sibling argField named in `field.dependsOn`, if any — e.g. this node's own Client field, to scope a Meeting field's options. */
  scopeValue?: string;
}) {
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // While typing (not yet a real picked client id), `value` IS the in-progress
  // search text for a freeSolo select-client field — searched server-side
  // instead of fetching the whole org up front (confirmed to run into the
  // hundreds). A real Mongo id (already picked, e.g. reopening a saved
  // workflow) is never treated as search text — see the select-client fetch
  // branch below for how its label still resolves even if it's not among the
  // default small list.
  const clientSearchText =
    field.type === "select-client" && isResolvedId(value) && !isMongoId(value) && !options.some((o) => o.id === value)
      ? value
      : "";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        let fetched: PickerOption[] = [];
        if (field.type === "select-client") {
          const clients = await fetchClientsForMentions(clientSearchText || undefined, clientSearchText ? 50 : 20);
          fetched = labelClientsForPicker(clients || []);
          // Already-picked client (e.g. reopening a saved workflow) that
          // isn't in the default small list — resolve its own name directly
          // rather than showing a blank/raw-id selection.
          if (isMongoId(value) && !fetched.some((o) => o.id === value)) {
            try {
              const existing: any = await fetchClientById(value);
              const name = existing?.name || existing?.data?.name;
              if (name) fetched = [{ id: value, label: name }, ...fetched];
            } catch {
              // best-effort — falls back to showing the raw id
            }
          }
        } else if (field.type === "select-meeting") {
          // Scoped to a sibling Client field's current value when this field
          // declares dependsOn — a real server-side filter, not fetch-all +
          // client-side narrowing.
          const scoped = field.dependsOn && isResolvedId(scopeValue);
          const res: any = scoped ? await fetchMeetingsByClient(scopeValue as string) : await fetchMeetings();
          const list = Array.isArray(res) ? res : res?.meetings || res?.data || [];
          fetched = list.map((m: any) => ({
            id: m._id || m.id,
            label: m.title ? `${m.title} — ${m.start?.date || ""}` : String(m._id || m.id),
          }));
        } else if (field.type === "select-template") {
          const res: any = await fetchAllDocTemplates();
          const list = Array.isArray(res) ? res : res?.data || [];
          fetched = list
            .filter((t: any) => !field.templateType || t.templateType === field.templateType)
            .map((t: any) => ({ id: t.templateId || t._id, label: t.templateName || t.name || "Untitled template" }));
        } else if (field.type === "select-contact") {
          const res: any = await fetchAllContacts();
          const list = Array.isArray(res) ? res : res?.data || [];
          fetched = list.map((c: any) => ({
            id: c._id || c.id,
            label: c.phone ? `${c.name} (${c.phone})` : c.name,
            phone: c.phone,
          }));
        } else if (field.type === "select-meeting-type") {
          const res: any = await fetchMeetingSettings();
          const list = Array.isArray(res) ? res : res?.data || [];
          const seen = new Set<string>();
          fetched = list
            .filter((m: any) => m.meetingType && !seen.has(m.meetingType) && seen.add(m.meetingType))
            .map((m: any) => ({ id: m.meetingType, label: m.meetingType }));
        } else if (field.type === "select") {
          fetched = (field.options || []).map((o) => ({ id: o.value, label: o.label }));
        }
        if (!cancelled) setOptions(fetched);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // "select-notes-folder"/"select-skills" manage their own fetch inside
    // NotesFolderPicker/SkillLibraryField — neither is handled by the
    // branches above, so running this generic loader for them would just be
    // a wasted no-op fetch dispatch.
    if (
      field.type &&
      field.type !== "text" &&
      field.type !== "textarea" &&
      field.type !== "select-notes-folder" &&
      field.type !== "select-skills"
    ) {
      debounceRef.current = setTimeout(load, clientSearchText ? 300 : 0);
    }
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.type, field.templateType, field.dependsOn, scopeValue, clientSearchText]);

  const optionsWithTemplate = useMemo(() => withTemplateOption(options, field), [options, field]);
  const showRequiredError = !!field.required && !value.trim();
  const requiredHelperText = showRequiredError ? "Please enter this value" : undefined;

  if (field.type === "select-notes-folder") {
    return (
      <div style={{ marginBottom: 16 }}>
        <NotesFolderPicker value={isMongoId(value) ? value : ""} onChange={onChange} label={field.label} />
      </div>
    );
  }

  if (field.type === "textarea") {
    // Inserts at the caret (or appends, if the field was never focused) rather
    // than always appending — matches how every other rich-text "insert
    // variable" affordance in the app behaves, and means clicking a chip
    // mid-sentence doesn't scramble word order.
    const insertToken = (token: string) => {
      const el = textareaRef.current;
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      const next = value.slice(0, start) + token + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(start + token.length, start + token.length);
      });
    };
    return (
      <div style={{ marginBottom: 16 }}>
        <TextField
          label={field.label}
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          error={showRequiredError}
          helperText={requiredHelperText}
          inputRef={textareaRef}
        />
        {field.insertTokens && field.insertTokens.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Insert:
            </Typography>
            {field.insertTokens.map((t) => (
              <Chip key={t.token} size="small" label={t.label} onClick={() => insertToken(t.token)} />
            ))}
          </Box>
        )}
      </div>
    );
  }

  if (field.type === "select-skills") {
    return <SkillLibraryField field={field} value={value} onChange={onChange} />;
  }

  if (field.type === "select-meeting-type") {
    const selectedIds = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];
    const selectedOptions = options.filter((o) => selectedIds.includes(o.id));
    return (
      <Autocomplete
        multiple
        size="small"
        loading={loading}
        options={options}
        value={selectedOptions}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={filterPickerOptions}
        onChange={(_, next) => onChange(next.map((o) => o.id).join(","))}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip size="small" label={option.label} {...getTagProps({ index })} key={option.id} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={field.label} placeholder={field.placeholder || "All meeting types"} />
        )}
        ListboxProps={{ style: { maxHeight: 260, overflowY: "auto" } }}
        sx={{ mb: 2 }}
      />
    );
  }

  if (
    field.type === "select" ||
    field.type === "select-client" ||
    field.type === "select-meeting" ||
    field.type === "select-contact" ||
    field.type === "select-template"
  ) {
    const selected = optionsWithTemplate.find((o) => o.id === value) || null;
    return (
      <Autocomplete
        size="small"
        freeSolo
        loading={loading}
        options={optionsWithTemplate}
        value={selected}
        inputValue={selected ? selected.label : value}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={filterPickerOptions}
        onChange={(_, next) => {
          if (!next) return onChange("");
          if (typeof next === "string") return onChange(next);
          const extra = field.syncToField && next.phone ? { [field.syncToField]: next.phone } : undefined;
          onChange(next.id, extra);
        }}
        onInputChange={(_, next, reason) => {
          // Free typing (e.g. a raw {{steps.x.output.y}} token) — only commit on
          // direct input, not when Autocomplete re-fires this for a selection.
          if (reason === "input") onChange(next);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={field.label}
            placeholder={field.placeholder}
            title={selected ? selected.label : undefined}
            error={showRequiredError}
            helperText={requiredHelperText}
          />
        )}
        // MUI's own default (40vh) can be tall enough to cover the rest of
        // the properties panel — or fields below it — on a long client/
        // meeting/template list. A fixed, modest window keeps it scrollable
        // in place instead of ballooning over neighbouring fields.
        ListboxProps={{ style: { maxHeight: 260, overflowY: "auto" } }}
        slotProps={{ popper: { placement: "bottom-start" } }}
        sx={{ mb: 2 }}
      />
    );
  }

  return (
    <TextField
      label={field.label}
      fullWidth
      size="small"
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => onChange(e.target.value)}
      error={showRequiredError}
      helperText={requiredHelperText}
      sx={{ mb: 2 }}
    />
  );
}
